import React from 'react'
import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';
import ShimmerCard from '../components/ShimmerCard';
import { rateLimitedFetch } from '../utils/apiWrapper';
import cacheManager from '../utils/cacheManager';
import { filterRecipesWithImages, isValidImageUrl } from '../utils/recipeUtils';

// Recipe card that hides itself if image is invalid or fails to load
function RecipeCard({ item, Card, CardLink, ImageWrapper, Gradient, CardContent, CardTitle, CardMeta, MetaBadge, MetaIcon }) {
  const [imageError, setImageError] = useState(false);

  // Hide if image URL is invalid or image failed to load
  if (!isValidImageUrl(item.image) || imageError) {
    return null;
  }

  return (
    <Card>
      <CardLink to={'/recipe/' + item.id}>
        <ImageWrapper>
          <img
            src={item.image}
            alt={item.title}
            onError={() => setImageError(true)}
          />
          <Gradient />
        </ImageWrapper>
        <CardContent>
          <CardTitle>{item.title}</CardTitle>
          <CardMeta>
            {item.readyInMinutes != null && item.readyInMinutes > 0 && (
              <MetaBadge>
                <MetaIcon><FiClock /></MetaIcon>
                {item.readyInMinutes}m
              </MetaBadge>
            )}
            {item.spoonacularScore != null && (
              <MetaBadge>
                <MetaIcon>⭐</MetaIcon>
                {Math.round(item.spoonacularScore)}
              </MetaBadge>
            )}
          </CardMeta>
        </CardContent>
      </CardLink>
    </Card>
  );
}

function Searched() {

  const [searchedRecipes, setSearchedRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterTime, setFilterTime] = useState('all');
  const [filterHealth, setFilterHealth] = useState('all');
  const [filterDiet, setFilterDiet] = useState('all');
  const [filterIntolerance, setFilterIntolerance] = useState('all');
  const [filterMaxCalories, setFilterMaxCalories] = useState('all');
  const [showAdvanced, setShowAdvanced] = useState(false);
  let params = useParams();
  const location = useLocation();

  const getSearched = async (name) => {
    setLoading(true);
    setError(null);

    // Normalize search term for cache key
    const searchKey = name.toLowerCase().trim();

    // Check cache first
    const cached = await cacheManager.get(searchKey, 'SEARCH');
    if (cached) {
      console.log('Using cached search results for:', name);
      setSearchedRecipes(cached);
      setFilteredRecipes(cached);
      setLoading(false);
      return;
    }

    try {
      const data = await rateLimitedFetch(`https://api.spoonacular.com/recipes/complexSearch?apiKey=${process.env.REACT_APP_API_KEY}&query=${name}&number=100&addRecipeInformation=true`)
      const recipes = await data.json();
      console.log('API Response:', recipes);

      // Handle API errors
      if (recipes.status === 'failure' || recipes.error) {
        console.error('API Error:', recipes.message || recipes.error);
        setSearchedRecipes([]);
        setFilteredRecipes([]);
        return;
      }

      // Filter out recipes without valid images
      const results = filterRecipesWithImages(recipes.results || []);
      console.log('Results count:', results.length);

      // Cache the results (7 day TTL)
      await cacheManager.set(searchKey, results, 'SEARCH');

      setSearchedRecipes(results);
      setFilteredRecipes(results);
    } catch (error) {
      console.error('Error fetching searched recipes:', error);

      // Handle rate limit errors
      if (error.message === 'API rate limit exceeded') {
        setError(`API limit reached. Daily: ${error.dailyCount}/${error.dailyLimit}, Monthly: ${error.monthlyCount}/${error.monthlyLimit}`);
      } else {
        setError('Failed to fetch recipes. Please try again.');
      }

      setSearchedRecipes([]);
      setFilteredRecipes([]);
    } finally {
      setLoading(false);
      // Dispatch event immediately when loading finishes
      window.dispatchEvent(new CustomEvent('search-complete'));
    }
  }

  useEffect(() => {
    // Extract search term from params (remove query string if present)
    const searchTerm = params.search?.split('?')[0] || params.search;
    if (searchTerm) {
      // Reset filters on new search
      setSortBy('relevance');
      setSortOrder('asc');
      setFilterTime('all');
      setFilterHealth('all');
      setFilterDiet('all');
      setFilterIntolerance('all');
      setFilterMaxCalories('all');
      getSearched(searchTerm);
    }
  }, [params.search, location.search, location.key]);


  useEffect(() => {
    let filtered = [...searchedRecipes];

    // Filter by time
    if (filterTime !== 'all') {
      const timeLimit = filterTime === 'quick' ? 30 : filterTime === 'medium' ? 60 : Infinity;
      filtered = filtered.filter(item => {
        const time = item.readyInMinutes != null ? item.readyInMinutes : Infinity;
        return time <= timeLimit;
      });
    }

    // Filter by health score
    if (filterHealth !== 'all') {
      const healthMin = filterHealth === 'high' ? 70 : filterHealth === 'medium' ? 50 : 0;
      filtered = filtered.filter(item => {
        const health = item.healthScore != null ? item.healthScore : 0;
        return health >= healthMin;
      });
    }

    // Filter by diet
    if (filterDiet !== 'all') {
      filtered = filtered.filter(item => {
        const diets = item.diets || [];
        switch(filterDiet) {
          case 'vegetarian': return item.vegetarian === true;
          case 'vegan': return item.vegan === true;
          case 'glutenFree': return item.glutenFree === true;
          case 'dairyFree': return item.dairyFree === true;
          case 'ketogenic': return diets.includes('ketogenic');
          case 'paleo': return diets.includes('paleolithic') || diets.includes('primal');
          default: return true;
        }
      });
    }

    // Filter by intolerance (exclude recipes with allergen)
    if (filterIntolerance !== 'all') {
      filtered = filtered.filter(item => {
        switch(filterIntolerance) {
          case 'gluten': return item.glutenFree === true;
          case 'dairy': return item.dairyFree === true;
          case 'egg':
            // Check if any ingredient contains egg
            return !item.extendedIngredients?.some(ing =>
              ing.name?.toLowerCase().includes('egg')
            );
          case 'peanut':
            return !item.extendedIngredients?.some(ing =>
              ing.name?.toLowerCase().includes('peanut')
            );
          case 'seafood':
            return !item.extendedIngredients?.some(ing =>
              ['fish', 'shrimp', 'salmon', 'tuna', 'crab', 'lobster', 'oyster', 'clam'].some(s =>
                ing.name?.toLowerCase().includes(s)
              )
            );
          default: return true;
        }
      });
    }

    // Filter by max calories
    if (filterMaxCalories !== 'all') {
      const maxCal = parseInt(filterMaxCalories);
      filtered = filtered.filter(item => {
        const calories = item.nutrition?.nutrients?.find(n => n.name === 'Calories')?.amount;
        return calories == null || calories <= maxCal;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch(sortBy) {
        case 'time':
          comparison = (a.readyInMinutes != null ? a.readyInMinutes : Infinity) - (b.readyInMinutes != null ? b.readyInMinutes : Infinity);
          break;
        case 'health':
          comparison = (a.healthScore != null ? a.healthScore : 0) - (b.healthScore != null ? b.healthScore : 0);
          break;
        case 'popularity':
          comparison = (a.spoonacularScore != null ? a.spoonacularScore : 0) - (b.spoonacularScore != null ? b.spoonacularScore : 0);
          break;
        case 'calories':
          const aCal = a.nutrition?.nutrients?.find(n => n.name === 'Calories')?.amount || 0;
          const bCal = b.nutrition?.nutrients?.find(n => n.name === 'Calories')?.amount || 0;
          comparison = aCal - bCal;
          break;
        default:
          return 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredRecipes(filtered);
  }, [searchedRecipes, sortBy, sortOrder, filterTime, filterHealth, filterDiet, filterIntolerance, filterMaxCalories]);

  return (
    <Container>
      <PageTitle>Search Results: {params.search}</PageTitle>
      {error && (
        <ErrorBanner>
          <ErrorText>{error}</ErrorText>
        </ErrorBanner>
      )}
      {loading ? (
        <Grid>
          {Array.from({ length: 12 }).map((_, index) => (
            <ShimmerCard key={`shimmer-${index}`} />
          ))}
        </Grid>
      ) : searchedRecipes.length === 0 ? (
        <EmptyState>
          <EmptyStateText>No recipes found. Try a different search term.</EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <FilterBar>
            <FilterGroup>
              <FilterLabel>Sort:</FilterLabel>
              <SortContainer>
                <FilterSelect
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    if (e.target.value === 'relevance') {
                      setSortOrder('asc');
                    }
                  }}
                >
                  <option value="relevance">Relevance</option>
                  <option value="time">Prep Time</option>
                  <option value="health">Health Score</option>
                  <option value="popularity">Score</option>
                  <option value="calories">Calories</option>
                </FilterSelect>
                {sortBy !== 'relevance' && (
                  <SortToggle
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    title={`Sort ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </SortToggle>
                )}
              </SortContainer>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Time:</FilterLabel>
              <FilterSelect value={filterTime} onChange={(e) => setFilterTime(e.target.value)}>
                <option value="all">All</option>
                <option value="quick">≤ 30 min</option>
                <option value="medium">≤ 60 min</option>
                <option value="long">60+ min</option>
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Diet:</FilterLabel>
              <FilterSelect value={filterDiet} onChange={(e) => setFilterDiet(e.target.value)}>
                <option value="all">All</option>
                <option value="vegetarian">Vegetarian</option>
                <option value="vegan">Vegan</option>
                <option value="glutenFree">Gluten-Free</option>
                <option value="dairyFree">Dairy-Free</option>
                <option value="ketogenic">Keto</option>
                <option value="paleo">Paleo</option>
              </FilterSelect>
            </FilterGroup>
            <AdvancedToggle onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? '− Less' : '+ More'}
            </AdvancedToggle>
            <ResultCount>{filteredRecipes.length} recipes</ResultCount>
          </FilterBar>
          {showAdvanced && (
            <FilterBar>
              <FilterGroup>
                <FilterLabel>Intolerance:</FilterLabel>
                <FilterSelect value={filterIntolerance} onChange={(e) => setFilterIntolerance(e.target.value)}>
                  <option value="all">None</option>
                  <option value="gluten">Gluten</option>
                  <option value="dairy">Dairy</option>
                  <option value="egg">Egg</option>
                  <option value="peanut">Peanut</option>
                  <option value="seafood">Seafood</option>
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Max Calories:</FilterLabel>
                <FilterSelect value={filterMaxCalories} onChange={(e) => setFilterMaxCalories(e.target.value)}>
                  <option value="all">Any</option>
                  <option value="300">≤ 300 cal</option>
                  <option value="500">≤ 500 cal</option>
                  <option value="700">≤ 700 cal</option>
                  <option value="1000">≤ 1000 cal</option>
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Health:</FilterLabel>
                <FilterSelect value={filterHealth} onChange={(e) => setFilterHealth(e.target.value)}>
                  <option value="all">All</option>
                  <option value="high">High (70+)</option>
                  <option value="medium">Medium (50+)</option>
                </FilterSelect>
              </FilterGroup>
            </FilterBar>
          )}
          {filteredRecipes.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No recipes match your current filters. Try adjusting your filters.</EmptyStateText>
            </EmptyState>
          ) : (
            <Grid>
              {filteredRecipes.map((item) => (
                <RecipeCard
                  key={item.id}
                  item={item}
                  Card={Card}
                  CardLink={CardLink}
                  ImageWrapper={ImageWrapper}
                  Gradient={Gradient}
                  CardContent={CardContent}
                  CardTitle={CardTitle}
                  CardMeta={CardMeta}
                  MetaBadge={MetaBadge}
                  MetaIcon={MetaIcon}
                />
              ))}
            </Grid>
          )}
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  max-width: 1200px;
  margin: var(--spacing-md) auto;
  padding: 0 var(--spacing-lg);

  @media screen and (max-width: 900px) {
    padding: 0 var(--spacing-sm);
    margin: var(--spacing-sm) auto;
  }
`

const PageTitle = styled.h2`
  font-size: 1.625rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
  letter-spacing: -0.02em;

  @media screen and (max-width: 900px) {
    font-size: 1.375rem;
    text-align: center;
  }
`

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 240px;
  padding: var(--spacing-xl);
`

const EmptyStateText = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  text-align: center;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--spacing-md);
  align-items: stretch;

  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: var(--spacing-sm);
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-xs);
  }
`

const Card = styled.div`
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border-subtle);
  transition: transform var(--transition-fast), background var(--transition-fast), border-color var(--transition-fast), box-shadow var(--transition-fast);
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-3px);
    background: var(--color-surface-elevated);
    border-color: var(--color-border);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }
`

const CardLink = styled(Link)`
  text-decoration: none;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 66.67%;
  overflow: hidden;
  background: var(--color-border);

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CardContent = styled.div`
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 88px;
  flex: 1;
`

const CardMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
`

const MetaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
`

const MetaIcon = styled.span`
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
`


const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  flex-wrap: wrap;

  @media screen and (max-width: 900px) {
    gap: var(--spacing-xs);
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: var(--spacing-xs);
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-text-secondary);
  white-space: nowrap;
`

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FilterSelect = styled.select`
  padding: 8px 12px;
  background: var(--color-surface);
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-text-primary);
  font-size: 0.9375rem;
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-surface-elevated);
  }

  &:focus {
    outline: none;
    background: var(--color-surface-elevated);
  }
`

const SortToggle = styled.button`
  padding: 6px 8px;
  background: var(--color-surface);
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  font-size: 0.6875rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--color-surface-elevated);
    color: var(--color-text-primary);
  }

  &:active {
    transform: scale(0.95);
  }
`

const AdvancedToggle = styled.button`
  padding: 8px 14px;
  background: transparent;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;

  &:hover {
    border-color: var(--color-text-tertiary);
    color: var(--color-text-secondary);
  }
`

const ResultCount = styled.div`
  margin-left: auto;
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);

  @media screen and (max-width: 900px) {
    margin-left: 0;
    font-size: 0.875rem;
  }
`

const CardTitle = styled.h4`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;

  @media screen and (max-width: 900px) {
    font-size: 0.9375rem;
  }
`

const Gradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background: linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%);
  z-index: 1;
  pointer-events: none;
`

const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.08);
  border-radius: var(--radius-sm);
  padding: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
`

const ErrorText = styled.p`
  color: #f87171;
  font-size: 0.8125rem;
  margin: 0;
  text-align: center;
`

export default Searched
